import { Accordion } from "@mui/material";
import { HomepageDownloadSummary, HomepageUploadSummary } from "../../../graphql/types";
import { DownloadRow } from "./DownloadRow";
import { UploadRow } from "./UploadRow";

type AccordionDetailsProps = {
  downloads?: Omit<HomepageDownloadSummary, "status" | "keys">[];
  uploads?: Omit<HomepageUploadSummary, "status" | "keys" | "dependencies">[];
  sparklineFirstDate: string;
  refreshData: () => Promise<void>;
  refreshing: boolean;
};

const AccordionDetails = ({
  downloads,
  uploads,
  sparklineFirstDate,
  refreshData,
  refreshing,
}: AccordionDetailsProps) => {
  const isDownloadEven = (downloads?.length ?? 0) % 2 === 0;
  return (
    <>
      {downloads?.map((download, index) => (
        <DownloadRow
          key={download.name}
          download={download}
          isDark={index % 2 === 0}
          sparklineFirstDate={sparklineFirstDate}
          refreshData={refreshData}
          refreshing={refreshing}
        />
      ))}
      {uploads?.map((upload, index) => (
        <UploadRow
          key={upload.name}
          upload={upload}
          isDark={isDownloadEven ? index % 2 === 0 : index % 2 !== 0}
          sparklineFirstDate={sparklineFirstDate}
          refreshData={refreshData}
          refreshing={refreshing}
        />
      ))}
    </>
  );
};

type GroupingAccordionProps = {
  downloads: Omit<HomepageDownloadSummary, "status" | "keys">[];
  uploads: Omit<HomepageUploadSummary, "status" | "keys" | "dependencies">[];
  children: React.ReactNode;
  sparklineFirstDate: string;
  index: number;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  refreshData: () => Promise<void>;
  refreshing: boolean;
};

const GroupingAccordion = ({
  downloads,
  uploads,
  children,
  sparklineFirstDate,
  expanded,
  setExpanded,
  refreshData,
  refreshing,
}: GroupingAccordionProps) => {
  return (
    <Accordion
      disableGutters
      expanded={expanded}
      sx={(theme) => ({
        borderRadius: "9px",
        border: `1px solid ${theme.palette.surfaceLight40.main}`,
        boxShadow: "none",
        "&:first-of-type": {
          borderTopRightRadius: "9px",
          borderTopLeftRadius: "9px",
        },
        "&:last-of-type": {
          borderBottomRightRadius: "9px",
          borderBottomLeftRadius: "9px",
        },
        "&::before": {
          content: "none",
        },
      })}
      onChange={() => setExpanded(!expanded)}
      slotProps={{
        transition: {
          mountOnEnter: true,
        },
      }}
    >
      {children}
      <AccordionDetails
        downloads={downloads}
        uploads={uploads}
        sparklineFirstDate={sparklineFirstDate}
        refreshData={refreshData}
        refreshing={refreshing}
      />
    </Accordion>
  );
};

export { GroupingAccordion };
